import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    CheckCheck,
    Trash2,
    X,
    Users,
    CheckSquare,
    AlertTriangle,
    ClipboardList,
    FileText,
    Clock,
    ChevronLeft,
    Filter,
    Loader2,
    MessageSquare
} from 'lucide-react';
import { useAdminNotifications } from '../context/AdminNotificationContext';

const AdminNotificationsPage = () => {
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications
    } = useAdminNotifications();

    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'employees', 'tasks', 'messages', 'surveys'
    const pageSize = 10;

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        // Filter and paginate notifications
        let filtered = [...notifications];
        
        if (filter === 'unread') {
            filtered = filtered.filter(n => !n.is_read);
        } else if (filter === 'employees') {
            filtered = filtered.filter(n => n.notification_type === 'employee_added');
        } else if (filter === 'tasks') {
            filtered = filtered.filter(n => 
                n.notification_type?.includes('task')
            );
        } else if (filter === 'messages') {
            filtered = filtered.filter(n => n.notification_type === 'message_received');
        } else if (filter === 'surveys') {
            filtered = filtered.filter(n => n.notification_type?.includes('survey'));
        }

        setDisplayedNotifications(filtered.slice(0, page * pageSize));
    }, [notifications, page, filter]);

    const hasMore = displayedNotifications.length < notifications.filter(n => {
        if (filter === 'unread') return !n.is_read;
        if (filter === 'employees') return n.notification_type === 'employee_added';
        if (filter === 'tasks') return n.notification_type?.includes('task');
        if (filter === 'messages') return n.notification_type === 'message_received';
        if (filter === 'surveys') return n.notification_type?.includes('survey');
        return true;
    }).length;

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'employee_added':
                return <Users size={20} className="text-blue-500" />;
            case 'task_completed':
                return <CheckSquare size={20} className="text-green-500" />;
            case 'task_overdue':
                return <AlertTriangle size={20} className="text-red-500" />;
            case 'compliance_violation':
                return <AlertTriangle size={20} className="text-orange-500" />;
            case 'survey_completed':
                return <ClipboardList size={20} className="text-purple-500" />;
            case 'task_assigned':
                return <FileText size={20} className="text-blue-500" />;
            case 'message_received':
                return <MessageSquare size={20} className="text-primary" />;
            default:
                return <Bell size={20} className="text-gray-500" />;
        }
    };

    const getNotificationBg = (type) => {
        switch (type) {
            case 'employee_added':
                return 'bg-blue-50';
            case 'task_completed':
                return 'bg-green-50';
            case 'task_overdue':
                return 'bg-red-50';
            case 'compliance_violation':
                return 'bg-orange-50';
            case 'survey_completed':
                return 'bg-purple-50';
            case 'message_received':
                return 'bg-primary/10';
            default:
                return 'bg-gray-50';
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return created.toLocaleDateString();
    };

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        if (notification.notification_type === 'message_received') {
            navigate('/admin/messages');
        } else {
            switch (notification.entity_type) {
                case 'employee':
                    navigate('/admin/employees');
                    break;
                case 'task':
                    navigate('/admin/tasks');
                    break;
                case 'survey':
                    navigate('/admin/surveys');
                    break;
                case 'compliance':
                    navigate('/admin/compliance');
                    break;
                case 'message':
                    navigate('/admin/messages');
                    break;
                default:
                    break;
            }
        }
    };

    const getTypeLabel = (notification) => {
        switch (notification.notification_type) {
            case 'employee_added':
                return 'employee';
            case 'task_completed':
            case 'task_overdue':
            case 'task_assigned':
                return 'task';
            case 'survey_completed':
                return 'survey';
            case 'message_received':
                return 'message';
            case 'compliance_violation':
                return 'compliance';
            default:
                return 'notification';
        }
    };

    const filterOptions = [
        { value: 'all', label: 'All' },
        { value: 'unread', label: 'Unread' },
        { value: 'employees', label: 'Employees' },
        { value: 'tasks', label: 'Tasks' },
        { value: 'messages', label: 'Messages' },
        { value: 'surveys', label: 'Surveys' }
    ];

    return (
        <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronLeft size={20} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Bell size={24} className="text-primary" />
                            Notifications
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <CheckCheck size={16} />
                            Mark all read
                        </button>
                    )}
                    {notifications.length > 0 && (
                        <button
                            onClick={dismissAllNotifications}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 size={16} />
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                <Filter size={16} className="text-gray-400 flex-shrink-0" />
                {filterOptions.map(option => (
                    <button
                        key={option.value}
                        onClick={() => {
                            setFilter(option.value);
                            setPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                            filter === option.value
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {/* Notifications List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center">
                        <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
                        <p className="text-gray-500">Loading notifications...</p>
                    </div>
                ) : displayedNotifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <Bell size={48} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No notifications</p>
                        <p className="text-gray-400 text-sm mt-1">
                            {filter !== 'all' ? 'Try changing the filter' : "You're all caught up!"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="divide-y divide-gray-100">
                            {displayedNotifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors group ${
                                        !notification.is_read ? 'bg-blue-50/50' : ''
                                    }`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationBg(notification.notification_type)}`}>
                                            {getNotificationIcon(notification.notification_type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-base ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                    {notification.title}
                                                </p>
                                                {!notification.is_read && (
                                                    <span className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                {notification.message}
                                            </p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium capitalize">
                                                    {getTypeLabel(notification)}
                                                </span>
                                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                                    <Clock size={12} />
                                                    {formatTimeAgo(notification.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dismissNotification(notification.id);
                                            }}
                                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                            title="Dismiss"
                                        >
                                            <X size={16} className="text-gray-400" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Load More */}
                        {hasMore && (
                            <div className="p-4 border-t border-gray-100">
                                <button
                                    onClick={loadMore}
                                    className="w-full py-2.5 text-sm text-primary hover:text-primary-hover font-medium hover:bg-primary/5 rounded-lg transition-colors"
                                >
                                    Load more notifications
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminNotificationsPage;
